import styled from '@emotion/styled';
import {
  palette,
  gradient,
  radius,
  shadow,
  spacing,
  duration,
  easing,
  layout,
} from '../../style-utils/theme';
import { spin } from '../../style-utils/motion';
import { SCALE } from '../../motion/tokens';

/**
 * Resolve the button fill:
 *  - disabled → muted lineStrong
 *  - explicit `transparent` → ghost/secondary (back buttons)
 *  - anything else (brand iris) → the primary gradient sheen
 */
const resolveFill = (background?: string, disabled?: boolean): string => {
  if (disabled) return palette.lineStrong;
  if (background === 'transparent') return 'transparent';

  return gradient.primary;
};

/** Arrow / icon slot — nudges on hover to hint direction. */
export const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  transition: transform ${duration.fast} ${easing.standard};
`;

export const ButtonContent = styled.span<{ $hidden: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

/* The success tick sits exactly where the ring does — absolute, over the
   hidden label — so a Continue that turns into a tick is still the same width.
   Sized to the label's cap height rather than the ring's 16px so it reads as
   text-weight, not as a badge. */
export const SuccessMark = styled.span`
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;

  & > svg {
    width: ${spacing.md};
    height: ${spacing.md};
  }
`;

export const LoadingIndicator = styled.span`
  position: absolute;
  width: ${spacing.sm};
  height: ${spacing.sm};
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: ${radius.circle};
  animation: ${spin} ${duration.slow} linear infinite;
`;

export const Button = styled.button<{
  background?: string;
  width?: string;
  disabled: boolean;
}>`
  z-index: 3;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  box-sizing: border-box;
  min-width: ${({ width }) => (width ? `${width}px` : '96px')};
  position: relative;
  height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border-radius: ${radius.sm};
  background: ${({ background, disabled }) =>
    resolveFill(background, disabled)};
  border: ${({ background, disabled }) =>
    !disabled && background === 'transparent'
      ? `1px solid ${palette.line}`
      : '1px solid transparent'};
  box-shadow: ${({ background, disabled }) =>
    disabled || background === 'transparent' ? 'none' : shadow.brand};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: transform ${duration.base} ${easing.spring},
    box-shadow ${duration.base} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard};

  &:hover {
    ${({ disabled, background }) =>
      disabled
        ? ''
        : background === 'transparent'
        ? `background: ${palette.irisTintSoft}; border-color: ${palette.lagoon};`
        : `background: ${gradient.primaryHover}; box-shadow: ${shadow.brandHover}; transform: translateY(-1px);`}
  }

  &:hover .nav-arrow-forward {
    transform: translateX(2px);
  }

  &:hover .nav-arrow-back {
    transform: translateX(-2px);
  }

  &:active {
    transform: scale(${SCALE.pressed});
    transition-duration: ${duration.instant};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;
